<template>
  <div class="home-page">
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>
    <div class="bg-glow bg-glow-2"></div>

    <header class="hero">
      <div class="hero-label">{{ $i18n.t('home.label') }}</div>
      <h1 class="hero-title">
        <span class="title-line">{{ $i18n.t('home.title') }}</span>
        <span class="title-accent">{{ $i18n.t('home.titleAccent') }}</span>
      </h1>
      <p class="hero-subtitle">{{ $i18n.t('home.subtitle') }}</p>
    </header>

    <section class="tools-section">
      <div class="tools-grid">
        <component
          v-for="(tool, index) in tools"
          :key="tool.path"
          :is="tool.static ? 'a' : 'router-link'"
          v-bind="tool.static ? { href: tool.path } : { to: tool.path }"
          class="tool-card"
          :style="{ animationDelay: `${index * 80}ms` }"
        >
          <div class="card-glow"></div>
          <div class="tool-icon" v-html="tool.icon"></div>
          <div class="tool-content">
            <h3 class="tool-name">{{ $i18n.t(tool.nameKey) }}</h3>
            <p class="tool-desc">{{ $i18n.t(tool.descKey) }}</p>
          </div>
          <div class="card-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
        </component>
      </div>
    </section>

    <footer class="footer">
      <div class="footer-content">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" class="icp-link">
          {{ $i18n.t('home.footer.icp') }}
        </a>
        <span class="footer-divider">|</span>
        <a href="mailto:zane4j@gmail.com" class="contact-link">{{ $i18n.t('home.footer.contact') }}</a>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  inject: ['i18n'],
  data() {
    return {
      tools: [
        {
          path: '/qrcode',
          nameKey: 'tools.qrcode.name',
          descKey: 'tools.qrcode.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="14" width="3" height="3"/><rect x="14" y="18" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/></svg>'
        },
        {
          path: '/json-converter',
          nameKey: 'tools.json.name',
          descKey: 'tools.json.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/></svg>'
        },
        {
          path: '/password-generator',
          nameKey: 'tools.password.name',
          descKey: 'tools.password.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>'
        },
        {
          path: '/timestamp-converter',
          nameKey: 'tools.timestamp.name',
          descKey: 'tools.timestamp.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><line x1="2" y1="12" x2="22" y2="12"/></svg>'
        },
        {
          path: '/base64-converter',
          nameKey: 'tools.base64.name',
          descKey: 'tools.base64.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/><rect x="8" y="9" width="8" height="6" rx="1"/></svg>'
        },
        {
          path: '/url-encoder-decoder',
          nameKey: 'tools.url.name',
          descKey: 'tools.url.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>'
        },
        {
          path: '/hash-generator',
          nameKey: 'tools.hash.name',
          descKey: 'tools.hash.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>'
        },
        {
          path: '/uuid-generator',
          nameKey: 'tools.uuid.name',
          descKey: 'tools.uuid.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 7h.01M7 12h.01M7 17h.01M12 7h.01M12 12h.01M12 17h.01M17 7h.01M17 12h.01M17 17h.01"/></svg>'
        },
        {
          path: '/cron-generator',
          nameKey: 'tools.cron.name',
          descKey: 'tools.cron.desc',
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>'
        },
        {
          path: '/dictation.html',
          nameKey: 'tools.dictation.name',
          descKey: 'tools.dictation.desc',
          static: true,
          icon: '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 14a8 8 0 0 1 16 0"/><path d="M18 19c0 1.1-.9 2-2 2h-1v-7h3v5Z"/><path d="M6 19c0 1.1.9 2 2 2h1v-7H6v5Z"/><path d="M12 4v3"/></svg>'
        }
      ]
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  padding: 60px 20px 32px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(37, 99, 235, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(37, 99, 235, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%);
  top: -150px;
  right: -150px;
}

.bg-glow-2 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%);
  bottom: -100px;
  left: -100px;
}

.hero {
  text-align: center;
  margin-bottom: 48px;
  position: relative;
  z-index: 1;
}

.hero-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 4px;
  color: var(--accent-primary);
  margin-bottom: 20px;
  opacity: 0;
  animation: fadeSlideUp 0.6s ease forwards;
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(48px, 10vw, 96px);
  line-height: 1;
  margin-bottom: 16px;
  letter-spacing: -2px;
}

.title-line {
  display: inline;
  color: var(--text-primary);
  opacity: 0;
  animation: fadeSlideUp 0.6s ease 0.1s forwards;
}

.title-accent {
  display: inline;
  color: var(--accent-primary);
  opacity: 0;
  animation: fadeSlideUp 0.6s ease 0.2s forwards;
}

.hero-subtitle {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 400;
  color: var(--text-secondary);
  letter-spacing: 0.5px;
  opacity: 0;
  animation: fadeSlideUp 0.6s ease 0.3s forwards;
}

.tools-section {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.tool-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  animation: fadeSlideUp 0.5s ease forwards;
  overflow: hidden;
}

.tool-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--accent-primary), var(--accent-success));
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.tool-card:hover::before {
  transform: scaleX(1);
}

.tool-card:hover {
  border-color: var(--accent-primary);
  transform: translateY(-3px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.card-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, var(--accent-primary) 0%, transparent 70%);
  opacity: 0;
  transform: translate(-50%, -50%);
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.tool-card:hover .card-glow {
  opacity: 0.04;
}

.tool-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border-radius: 10px;
  color: var(--accent-primary);
  transition: all 0.3s ease;
}

.tool-card:hover .tool-icon {
  background: var(--accent-primary);
  color: var(--bg-primary);
}

.tool-content {
  flex: 1;
  min-width: 0;
}

.tool-name {
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.tool-desc {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 400;
}

.card-arrow {
  flex-shrink: 0;
  color: var(--text-secondary);
  transform: translateX(-8px);
  opacity: 0;
  transition: all 0.3s ease;
}

.tool-card:hover .card-arrow {
  transform: translateX(0);
  opacity: 1;
  color: var(--accent-primary);
}

.footer {
  margin-top: 60px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  border-top: 1px solid var(--border-color);
}

.icp-link,
.contact-link {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s ease;
}

.icp-link:hover,
.contact-link:hover {
  color: var(--accent-primary);
}

.footer-divider {
  color: var(--border-color);
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .home-page {
    padding: 48px 16px 28px;
  }

  .hero {
    margin-bottom: 40px;
  }

  .hero-title {
    font-size: clamp(40px, 10vw, 72px);
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .tools-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .tool-card {
    padding: 16px 20px;
    gap: 16px;
  }

  .tool-icon {
    width: 44px;
    height: 44px;
  }

  .tool-name {
    font-size: 15px;
  }

  .footer {
    margin-top: 48px;
  }
}

@media (max-width: 480px) {
  .tool-card {
    padding: 14px 18px;
  }

  .tool-icon {
    width: 40px;
    height: 40px;
  }

  .tool-name {
    font-size: 14px;
  }

  .tool-desc {
    font-size: 13px;
  }
}
</style>
